<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import PageLayout from 'dashboard/components-next/metaAgent/PageLayout.vue';
import StatusCard from 'dashboard/components-next/metaAgent/StatusCard.vue';
import BusinessInfoForm from 'dashboard/components-next/metaAgent/BusinessInfoForm.vue';
import FaqList from 'dashboard/components-next/metaAgent/FaqList.vue';
import WebsiteList from 'dashboard/components-next/metaAgent/WebsiteList.vue';
import FileList from 'dashboard/components-next/metaAgent/FileList.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const agent = useMapGetter('metaAgent/getAgent');
const faqs = useMapGetter('metaAgent/getFaqs');
const websites = useMapGetter('metaAgent/getWebsites');
const files = useMapGetter('metaAgent/getFiles');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const activeTab = ref(0);

const inboxId = computed(() => Number(route.params.inboxId));
const isOnboarded = computed(
  () => Boolean(agent.value.id) && agent.value.lifecycle !== 'pending_setup'
);

const tabs = computed(() => [
  { label: t('META_AGENT.KNOWLEDGE.TABS.BUSINESS_INFO') },
  { label: t('META_AGENT.KNOWLEDGE.TABS.FAQS'), count: faqs.value.length },
  {
    label: t('META_AGENT.KNOWLEDGE.TABS.WEBSITES'),
    count: websites.value.length,
  },
  { label: t('META_AGENT.KNOWLEDGE.TABS.FILES'), count: files.value.length },
]);

// Meta's `detail` names the offending field; the generic error is the fallback.
const alertFailure = error => {
  const data = error?.response?.data || {};
  useAlert(data.detail || data.error || t('META_AGENT.KNOWLEDGE.ERROR'));
};

// A failed load must say so: without this a 502 from Meta leaves every tab
// silently empty, which reads as "this agent has no knowledge".
const load = async () => {
  try {
    await store.dispatch('metaAgent/getInboxes');
    await store.dispatch('metaAgent/getAgent', inboxId.value);
    if (isOnboarded.value) {
      await store.dispatch('metaAgent/getKnowledge', inboxId.value);
    }
  } catch (error) {
    alertFailure(error);
  }
};

// Returns whether the write landed, so a dialog can stay open on failure
// instead of discarding what the operator typed.
const run = async (action, payload, successKey) => {
  try {
    await store.dispatch(action, { inboxId: inboxId.value, ...payload });
    useAlert(t(successKey));
    return true;
  } catch (error) {
    alertFailure(error);
    return false;
  }
};

const saveBusinessInfo = businessInfo =>
  run(
    'metaAgent/updateBusinessInfo',
    { businessInfo },
    'META_AGENT.KNOWLEDGE.BUSINESS_INFO.SUCCESS'
  );

const saveFaq = payload =>
  run('metaAgent/saveFaq', payload, 'META_AGENT.KNOWLEDGE.FAQS.SUCCESS');

const deleteFaq = faqId =>
  run('metaAgent/deleteFaq', { faqId }, 'META_AGENT.KNOWLEDGE.FAQS.DELETED');

const addWebsite = url =>
  run('metaAgent/addWebsite', { url }, 'META_AGENT.KNOWLEDGE.WEBSITES.SUCCESS');

const deleteWebsite = websiteId =>
  run(
    'metaAgent/deleteWebsite',
    { websiteId },
    'META_AGENT.KNOWLEDGE.WEBSITES.DELETED'
  );

// Re-reads the list only; Meta gives no way to trigger a crawl.
const refreshWebsites = async () => {
  try {
    await store.dispatch('metaAgent/getWebsites', inboxId.value);
  } catch (error) {
    alertFailure(error);
  }
};

const uploadFile = file =>
  run('metaAgent/uploadFile', { file }, 'META_AGENT.KNOWLEDGE.FILES.SUCCESS');

const deleteFile = fileId =>
  run('metaAgent/deleteFile', { fileId }, 'META_AGENT.KNOWLEDGE.FILES.DELETED');

onMounted(load);
watch(inboxId, load);
</script>

<template>
  <PageLayout
    :header-title="t('META_AGENT.KNOWLEDGE.HEADER')"
    :is-fetching="uiFlags.fetchingAgent || uiFlags.fetchingKnowledge"
  >
    <StatusCard
      v-if="!isOnboarded"
      variant="warning"
      :title="t('META_AGENT.OVERVIEW.ONBOARD.TITLE')"
      :description="t('META_AGENT.KNOWLEDGE.NOT_ONBOARDED')"
    />
    <div v-else class="flex flex-col gap-6">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTab"
        @tab-changed="
          activeTab = tabs.findIndex(tab => tab.label === $event.label)
        "
      />
      <BusinessInfoForm v-if="activeTab === 0" @save="saveBusinessInfo" />
      <FaqList
        v-else-if="activeTab === 1"
        :save-handler="saveFaq"
        @delete="deleteFaq"
      />
      <WebsiteList
        v-else-if="activeTab === 2"
        @add="addWebsite"
        @delete="deleteWebsite"
        @refresh="refreshWebsites"
      />
      <FileList v-else @upload="uploadFile" @delete="deleteFile" />
    </div>
  </PageLayout>
</template>
